/**
 * GeoJSON feature and route color formatting utilities.
 *
 * Consolidates feature transformation functions from:
 *   - src/utils/route.js (formatStopFeatures, formatUnassignedStopFeatures)
 *   - src/components/route-manager/BulkActions/settings.js (formatRoutesWithContrastColors, formatGeojsonDataBetweenDays)
 *
 * All functions are pure — MUI theme is injected as a parameter where needed.
 */
import { common, orange } from '@mui/material/colors'
import { get } from 'lodash'

import { WORK_ORDER_STATUS_DISPLAY } from 'settings/constants/service'
import { isValidHex } from 'utils/validations'
import { stopStatus } from 'utils/workOrderStatus'

/**
 * Enriches GeoJSON stop features with route colors, marker icons, and contrast colors.
 *
 * Moved from utils/route.js lines 150-176.
 *
 * @param {Object[]} features - GeoJSON Feature array
 * @param {Object} theme - MUI theme (for palette.getContrastText)
 * @returns {Object[]} enriched features
 */
export const formatStopFeatures = (features, theme) =>
  features.map((feature, index) => {
    const properties = get(feature, 'properties', {})
    const color = get(properties, 'color', common.black)
    const formattedColor = isValidHex(color) ? color : common.black
    const routeTextColor = theme.palette.getContrastText(formattedColor)
    const workOrderStatus = stopStatus(properties)
    const marker = get(WORK_ORDER_STATUS_DISPLAY, `${workOrderStatus}.marker`)
    const markerTextColor = get(WORK_ORDER_STATUS_DISPLAY, `${workOrderStatus}.markerTextColor`)
    const workOrderColor = get(WORK_ORDER_STATUS_DISPLAY, `${workOrderStatus}.borderColor`)
    // Preserve a caller-supplied `to-be-assigned-marker` icon + its text
    // color. The caller (e.g. mapStopsToFeaturePoints) flags the pending
    // stop on purpose and this function would otherwise overwrite it with
    // the work-order-status marker, making the pending stop render in
    // route colors instead of the unassigned treatment.
    const incomingIcon = get(properties, 'icon')
    const isPending = incomingIcon === 'to-be-assigned-marker'
    return {
      ...feature,
      properties: {
        ...properties,
        'route-text-color': routeTextColor,
        'border-color': routeTextColor,
        icon: isPending ? incomingIcon : marker,
        'text-color': isPending ? get(properties, 'text-color', common.black) : markerTextColor,
        'work-order-color': workOrderColor,
        _originalColor: formattedColor,
        _originalContrastColor: routeTextColor,
        position: index + 1,
        originalPosition: index + 1,
      },
    }
  })

/**
 * Formats GeoJSON features with hardcoded unassigned styling.
 *
 * Moved from utils/route.js lines 178-202.
 *
 * @param {Object[]} features - GeoJSON Feature array
 * @returns {Object[]} features styled for unassigned state
 */
export const formatUnassignedStopFeatures = features =>
  features.map((feature, index) => {
    const properties = get(feature, 'properties', {})
    const workOrderStatus = stopStatus(properties)
    const workOrderColor = get(WORK_ORDER_STATUS_DISPLAY, `${workOrderStatus}.borderColor`)
    const markerTextColor = get(WORK_ORDER_STATUS_DISPLAY, `${workOrderStatus}.markerTextColor`)
    return {
      ...feature,
      properties: {
        ...properties,
        color: common.white,
        'route-text-color': common.black,
        'border-color': orange[300],
        icon: 'to-be-assigned-marker',
        'text-color': markerTextColor || common.black,
        'work-order-color': workOrderColor || common.white,
        _originalColor: common.white,
        _originalContrastColor: common.black,
        routeId: 'UNASSIGNED',
        position: index + 1,
        originalPosition: index + 1,
      },
    }
  })

/**
 * Validates route hex colors and computes contrast colors via theme.
 *
 * Moved from BulkActions/settings.js lines 571-577.
 *
 * @param {Object[]} routes - route objects with `color` property
 * @param {Object} theme - MUI theme
 * @returns {Object[]} routes with validated `color` and computed `contrastColor`
 */
export const formatRoutesWithContrastColors = (routes, theme) =>
  routes.map(route => {
    const color = get(route, 'color', common.black)
    const formattedColor = isValidHex(color) ? color : common.black
    const contrastColor = theme.palette.getContrastText(formattedColor)
    return { ...route, color: formattedColor, contrastColor }
  })

/** Adds the monochrome icon and a `date` property so the between-days map can tell the two days apart. */
export const formatGeojsonDataBetweenDays = (data, date, routeId, theme) => {
  const routeGeojsonData = get(data, 'routeGeojsonData', {})
  const moveFromRouteFeatures = get(routeGeojsonData, `${routeId}.stops.features`, [])
  const stopMarkers = formatStopFeatures(moveFromRouteFeatures, theme).map(marker => ({
    ...marker,
    properties: {
      ...marker?.properties,
      icon: 'monochrome-marker',
      date,
    },
  }))
  const sequenceLine = get(routeGeojsonData, `${routeId}.sequenceLine`, {})
  const containerFeatures = get(routeGeojsonData, `${routeId}.newContainers.features`, [])
  const routeDailyGeoJsonId = get(routeGeojsonData, `${routeId}.routeDailyGeoJsonId`)
  return { stopMarkers, sequenceLine, containerFeatures, routeDailyGeoJsonId }
}
